// Day0（本登録前・匿名）のチャンク分割録音。
// 本番の学習者提出・フィードバック録音と同じ ChunkedRecorder クラス（数秒おきにチャンクを切り出す
// MediaRecorderラッパー、動画時のプレビュー確認・向き補正セッションも共通）をそのまま使い、
// チャンクのアップロード先だけをDay0専用エンドポイントに差し替える。
// 本番のChunkUploaderが行う「presigned URLでR2へ直接PUT」ではなく、Node経由のプレーンなPOSTだが、
// 一時的な通信不安定でチャンク送信が1回失敗しても録音全体を失わないよう、本番同様に自動リトライする。
// 本番のIndexedDBへの永続化（ブラウザクラッシュ・タブ閉じからの復旧）は今回のスコープには含めていない。

import { ChunkedRecorder } from '../utils/chunkedRecorder.js'

const CHUNK_UPLOAD_MAX_ATTEMPTS = 4
const CHUNK_UPLOAD_RETRY_DELAYS_MS = [500, 1500, 3000] // 4回目の試行までの間隔

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** チャンク1つのアップロードを、失敗時は少し待って最大4回まで自動リトライする */
async function uploadChunkWithRetry(uploadChunk, chunkData, chunkInfo) {
  let lastError = null
  for (let attempt = 1; attempt <= CHUNK_UPLOAD_MAX_ATTEMPTS; attempt++) {
    try {
      await uploadChunk(chunkData, chunkInfo)
      return
    } catch (e) {
      lastError = e
      console.warn(`⚠️ Day0チャンクアップロード失敗（${attempt}/${CHUNK_UPLOAD_MAX_ATTEMPTS}回目）:`, chunkInfo.chunkIndex, e)
      if (attempt < CHUNK_UPLOAD_MAX_ATTEMPTS) {
        await sleep(CHUNK_UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 3000)
      }
    }
  }
  throw lastError
}

/**
 * @param {Object} opts
 * @param {(chunkData: Blob, chunkInfo: object) => Promise<void>} opts.uploadChunk チャンク1つを送る関数
 * @param {(stream: MediaStream) => void} [opts.onStreamReady] プレビュー用にstreamを受け取る
 */
export function createDay0ChunkRecorder({ uploadChunk, onStreamReady }) {
  let recorder = null
  let pendingUploads = []
  let uploadError = null
  let localChunksForPreview = [] // アップロードとは別に、端末側で見返せるプレビュー用に保持するだけの配列

  /** 音声のみ：録画開始前にマイク権限を確認しておく（映像のような見た目のプレビューは無いため、許可確認だけ先に済ませる） */
  async function prepareAudioPreview() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((t) => t.stop())
  }

  /** 動画のみ：録画開始前にカメラを確保し、プレビュー表示できる状態にする（本番と同じ二段階フロー） */
  async function prepareVideoPreview() {
    recorder = new ChunkedRecorder({
      day: 0,
      type: 'day0',
      userId: 'day0-anonymous',
      mediaKind: 'video',
      onStreamReady
    })
    await recorder.prepareVideoPreview()
    return recorder
  }

  /** @param {'audio'|'video'} mediaKind */
  async function start(mediaKind) {
    pendingUploads = []
    uploadError = null
    localChunksForPreview = []

    if (mediaKind === 'video' && !recorder) {
      await prepareVideoPreview()
    }
    if (!recorder) {
      recorder = new ChunkedRecorder({
        day: 0,
        type: 'day0',
        userId: 'day0-anonymous',
        mediaKind,
        onStreamReady
      })
    }

    recorder.onChunk = (chunkData, chunkInfo) => {
      localChunksForPreview.push(chunkData)
      const p = uploadChunkWithRetry(uploadChunk, chunkData, chunkInfo).catch((e) => {
        console.error(`❌ Day0チャンクアップロード失敗（${CHUNK_UPLOAD_MAX_ATTEMPTS}回リトライ済み）:`, e)
        uploadError = e
      })
      pendingUploads.push(p)
    }

    await recorder.start()
    return recorder
  }

  /** 録音を止め、送信済み・送信中の全チャンクのアップロード完了を待つ */
  function stop() {
    return new Promise((resolve, reject) => {
      if (!recorder) return reject(new Error('録音が開始されていません'))
      recorder.onComplete = async (recordingInfo) => {
        try {
          await Promise.all(pendingUploads)
          if (uploadError) throw uploadError
          const previewBlob = localChunksForPreview.length
            ? new Blob(localChunksForPreview, { type: localChunksForPreview[0].type || 'video/webm' })
            : null
          // iOS/SafariはWebM非対応のためmp4になる。サーバー側の結合処理をcodecTypeで切り替えるため呼び出し元に返す
          const codecType = recorder.codecConfig?.type === 'mp4' ? 'mp4' : 'webm'
          resolve({ recordingId: recordingInfo.recordingId, durationSeconds: recordingInfo.duration, previewBlob, codecType })
        } catch (e) {
          reject(e)
        } finally {
          recorder = null // 次のstart()で必ず新しいChunkedRecorderを作らせる（録り直し対応）
        }
      }
      recorder.stop()
    })
  }

  /** モーダルを閉じる時など、録画を始めずにプレビュー用カメラだけ解放したい場合に呼ぶ */
  function cancelPreview() {
    if (recorder?.portraitSession) {
      recorder.portraitSession.stop()
    } else if (recorder?.stream) {
      recorder.stream.getTracks().forEach((t) => t.stop())
    }
    recorder = null
  }

  return { prepareAudioPreview, prepareVideoPreview, start, stop, cancelPreview }
}
